class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.tpSyncTimer = null;
    this.tpUpdateInFlight = false;
    this.tpDesiredUpdates = new Map();
    this.tpOptimisticLines = new Map();

    this.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.matches('.tp-cart-quantity .quantity__input')) return;

      this.handleDrawerQuantityChange(input);
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '.js-contents',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /*
   * Do not disable or blur the drawer while Shopify is syncing.
   * The customer can keep clicking +/- while the final quantity is batched.
   */
  enableLoading(line) {
    const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
    lineItem?.setAttribute('data-cart-updating', 'true');

    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
    lineItem?.removeAttribute('data-cart-updating');

    this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`).forEach((overlay) => {
      overlay.classList.add('hidden');
    });
  }

  handleDrawerQuantityChange(input) {
    const quantity = Number.parseInt(input.value, 10);
    const line = String(input.dataset.index || '');
    const min = Number.parseInt(input.dataset.min || input.min || '0', 10);
    const max = input.max ? Number.parseInt(input.max, 10) : null;
    const step = Number.parseInt(input.step || '1', 10);

    let message = '';

    if (!Number.isFinite(quantity)) {
      message = window.cartStrings?.error || 'Please enter a valid quantity.';
    } else if (Number.isFinite(min) && quantity < min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', min);
    } else if (Number.isFinite(max) && quantity > max) {
      message = window.quickOrderListStrings.max_error.replace('[max]', max);
    } else if (Number.isFinite(step) && step > 0 && quantity % step !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', step);
    }

    if (message) {
      input.setCustomValidity(message);
      input.reportValidity();
      this.resetQuantityInput(line);
      return;
    }

    input.setCustomValidity('');
    input.reportValidity();

    // Update the visible cart immediately.
    this.applyOptimisticQuantity(input);

    // Store only the newest desired quantity for this line.
    this.tpDesiredUpdates.set(line, {
      line,
      quantity,
      name: 'updates[]',
      variantId: input.dataset.quantityVariantId,
      isRemove: quantity === 0,
    });

    // A rapid burst of clicks produces one Shopify request after the user pauses.
    window.clearTimeout(this.tpSyncTimer);
    this.tpSyncTimer = window.setTimeout(() => {
      this.flushDesiredUpdates();
    }, 300);
  }

  /*
   * Remove buttons still update immediately.
   */
  updateQuantity(line, quantity, event, name, variantId) {
    const isRemove = quantity === 0 || event?.currentTarget instanceof CartRemoveButton;

    if (!isRemove) {
      // Quantity-input changes are handled by handleDrawerQuantityChange().
      // Keep this fallback for any direct callers.
      this.tpDesiredUpdates.set(String(line), {
        line: String(line),
        quantity: Number(quantity),
        name: name || 'updates[]',
        variantId,
        isRemove: false,
      });

      window.clearTimeout(this.tpSyncTimer);
      this.tpSyncTimer = window.setTimeout(() => {
        this.flushDesiredUpdates();
      }, 300);

      return Promise.resolve(null);
    }

    window.clearTimeout(this.tpSyncTimer);
    this.tpDesiredUpdates.delete(String(line));

    return this.performServerUpdate({
      line: String(line),
      quantity: 0,
      name: name || 'updates[]',
      variantId,
      isRemove: true,
    });
  }

  async flushDesiredUpdates() {
    if (this.tpUpdateInFlight) return;

    const nextEntry = this.tpDesiredUpdates.entries().next();
    if (nextEntry.done) return;

    const [line, payload] = nextEntry.value;

    // Remove this exact desired state from the queue before the request.
    // If the shopper changes quantity again during the request, a fresh value
    // for the same line will be added back to the map.
    this.tpDesiredUpdates.delete(line);

    await this.performServerUpdate(payload);

    // If more changes arrived during the request, send only the latest state.
    if (this.tpDesiredUpdates.size > 0) {
      window.clearTimeout(this.tpSyncTimer);
      this.tpSyncTimer = window.setTimeout(() => {
        this.flushDesiredUpdates();
      }, 120);
    }
  }

  async performServerUpdate(payload) {
    if (this.tpUpdateInFlight) {
      this.tpDesiredUpdates.set(payload.line, payload);
      return null;
    }

    this.tpUpdateInFlight = true;
    let parsedState = null;

    try {
      const syntheticEvent = {
        currentTarget: payload.isRemove ? document.createElement('cart-remove-button') : null,
      };

      parsedState = await super.updateQuantity(
        payload.line,
        payload.quantity,
        syntheticEvent,
        payload.name,
        payload.variantId
      );

      if (parsedState?.errors) {
        // Shopify rejected the actual requested quantity. Do not reapply any
        // optimistic number over the server's validated value.
        this.tpDesiredUpdates.delete(payload.line);
        this.tpOptimisticLines.delete(payload.line);
        return parsedState;
      }

      if (parsedState?.item_count === 0) {
        await this.renderEmptyDrawer();
        return parsedState;
      }

      // The server response has just re-rendered this line. Reset our pricing
      // baseline to the confirmed Shopify values.
      this.tpOptimisticLines.delete(payload.line);

      // If the shopper clicked again while the request was running, restore
      // their newest desired quantity immediately on the freshly-rendered DOM.
      const newerPayload = this.tpDesiredUpdates.get(payload.line);
      if (newerPayload && newerPayload.quantity !== payload.quantity) {
        const freshInput = document.getElementById(`Drawer-quantity-${payload.line}`);

        if (freshInput) {
          freshInput.value = String(newerPayload.quantity);
          this.applyOptimisticQuantity(freshInput);
        }
      }
    } finally {
      this.tpUpdateInFlight = false;
    }

    return parsedState;
  }

  async renderEmptyDrawer() {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    if (!response.ok) return;

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
    const currentInner = document.querySelector('#CartDrawer .drawer__inner');

    if (sourceInner && currentInner) {
      currentInner.replaceWith(sourceInner);
    }
  }

  applyOptimisticQuantity(input) {
    const line = String(input.dataset.index || '');
    const row = input.closest('.cart-item');
    if (!line || !row) return;

    const newQuantity = Number.parseInt(input.value, 10);
    if (!Number.isFinite(newQuantity) || newQuantity < 0) return;

    let state = this.tpOptimisticLines.get(line);

    if (!state) {
      const serverQuantity = Number.parseInt(input.getAttribute('value') || input.value, 10) || 1;
      const currentPriceElement = row.querySelector('.tp-cart-current-price');
      const comparePriceElement = row.querySelector('.tp-cart-compare-price');

      const currentLineCents = this.parseMoney(currentPriceElement?.textContent);
      const compareLineCents = this.parseMoney(comparePriceElement?.textContent);

      if (!Number.isFinite(currentLineCents)) return;

      state = {
        lastQuantity: serverQuantity,
        unitPriceCents: currentLineCents / serverQuantity,
        unitCompareCents:
          Number.isFinite(compareLineCents) && compareLineCents > 0 ? compareLineCents / serverQuantity : null,
      };

      this.tpOptimisticLines.set(line, state);
    }

    const delta = newQuantity - state.lastQuantity;
    if (delta === 0) return;

    const currentPriceElement = row.querySelector('.tp-cart-current-price');
    const comparePriceElement = row.querySelector('.tp-cart-compare-price');
    const saveBadge = row.querySelector('.tp-cart-save-badge');

    if (currentPriceElement) {
      const linePrice = Math.round(state.unitPriceCents * newQuantity);
      currentPriceElement.textContent = this.formatMoneyLike(currentPriceElement.textContent, linePrice);
    }

    if (comparePriceElement && state.unitCompareCents != null) {
      const compareLinePrice = Math.round(state.unitCompareCents * newQuantity);
      comparePriceElement.textContent = this.formatMoneyLike(comparePriceElement.textContent, compareLinePrice);
    }

    if (saveBadge && state.unitCompareCents != null) {
      const savingCents = Math.max(
        0,
        Math.round((state.unitCompareCents - state.unitPriceCents) * newQuantity)
      );
      const currentBadgeMoney = saveBadge.textContent.replace(/^\s*SAVE\s*/i, '');
      saveBadge.textContent = `SAVE ${this.formatMoneyLike(currentBadgeMoney, savingCents)}`;
    }

    const totalElement = document.querySelector('.cart-drawer .totals__total-value');
    if (totalElement) {
      const currentTotal = this.parseMoney(totalElement.textContent);

      if (Number.isFinite(currentTotal)) {
        const nextTotal = Math.max(0, Math.round(currentTotal + delta * state.unitPriceCents));
        totalElement.textContent = this.formatMoneyLike(totalElement.textContent, nextTotal);
      }
    }

    if (state.unitCompareCents != null) {
      const savingsElement = document.querySelector('.tp-cart-savings strong');

      if (savingsElement) {
        const currentSavings = Math.abs(this.parseMoney(savingsElement.textContent) || 0);
        const unitSaving = Math.max(0, state.unitCompareCents - state.unitPriceCents);
        const nextSavings = Math.max(0, Math.round(currentSavings + delta * unitSaving));
        savingsElement.textContent = this.formatMoneyLike(savingsElement.textContent, nextSavings, true);
      }
    }

    state.lastQuantity = newQuantity;
  }

  parseMoney(value) {
    if (!value) return NaN;

    const raw = String(value).trim();
    const match = raw.match(/-?[\d][\d.,]*/);
    if (!match) return NaN;

    let number = match[0];
    const isNegative = raw.includes('-');

    const lastDot = number.lastIndexOf('.');
    const lastComma = number.lastIndexOf(',');
    const decimalSeparator = lastDot > lastComma ? '.' : lastComma > -1 ? ',' : null;

    if (decimalSeparator) {
      const parts = number.split(decimalSeparator);
      const decimalPart = parts.pop();
      const integerPart = parts.join('').replace(/[.,]/g, '');
      number = `${integerPart}.${decimalPart}`;
    } else {
      number = number.replace(/[.,]/g, '');
    }

    const parsed = Number.parseFloat(number);
    if (!Number.isFinite(parsed)) return NaN;

    return Math.round(parsed * 100) * (isNegative ? -1 : 1);
  }

  formatMoneyLike(template, cents, forceNegative = false) {
    const source = String(template || '').trim();
    const numericMatch = source.match(/[\d][\d.,]*/);
    const value = Math.max(0, Number(cents) || 0) / 100;

    if (!numericMatch) {
      return `${forceNegative ? '-' : ''}$${value.toFixed(2)}`;
    }

    const numeric = numericMatch[0];
    const prefix = source.slice(0, numericMatch.index).replace(/-/g, '');
    const suffix = source.slice(numericMatch.index + numeric.length);
    const decimalMatch = numeric.match(/[.,](\d+)$/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;

    const formattedNumber = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);

    return `${forceNegative ? '-' : ''}${prefix}${formattedNumber}${suffix}`;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
